import React, { useState, useEffect, useRef, useCallback } from 'react';
import { debounce } from '../../../../../../../candt/helpers/utilities';

import ArticleCardBase from '../../Base';
import { breakpoints } from '../../../../../config/constants';
import { inheritChildStyle } from '../../../../../helpers/text';

import './styles.scss';

const createSpanIndent = () => {
  const span = document.createElement('span');
  Object.assign(span.style, {
    width: '100px',
    color: 'transparent',
    display: 'inline-block'
  });
  span.innerHTML = '.';
  return span;
};

/**
 * Split the content of a node into
 * words and distribute the parent element
 * tags. Use in combination of GSAP SplitText
 * @param {HTMLElement} node - HTML node
 * @param {Boolean} includeRoot - flag if root element should be distributed also
 */
const splitIntoWords = (node, includeRoot) => {
  const { tagName, childNodes } = node;
  const words = [];
  childNodes.forEach(childNode => {
    if (!childNode.tagName)
      words.push(...childNode.nodeValue.split(' ').map(textNode => `<span>${textNode} </span>`));
    else if (childNode.tagName === 'BR') words.push('<br />');
    else words.push(...splitIntoWords(childNode, true));
  });

  if (!includeRoot) return words;

  let attributes = '';
  const tag = tagName.toLowerCase();
  for (const attribute of node.attributes) {
    attributes += ` ${attribute.name}="${attribute.value}"`;
  }
  return words.map(word => {
    if (word === '<br />') return word;
    return `<${tag}${attributes}>${word} </${tag}>`;
  });
};

const splitIntoLines = (node, indented) => {
  if (node.nodeType === window.Node.TEXT_NODE) return [];

  if (indented) node.prepend(createSpanIndent(), node.firstChild);
  const words = splitIntoWords(node, false).join('');

  // return an empty line if no words found
  if (!words)
    return [
      `<div style="position:relative;display:block;text-align:start;color:transparent">.</div>`
    ];

  node.innerHTML = words;
  node.style.whiteSpace = 'nowrap';

  const { childNodes, clientWidth } = node;
  const lines = [];

  let currentIndex = 0;
  let currentWidth = 0;

  childNodes.forEach(childNode => {
    const { offsetWidth, outerHTML, tagName } = childNode;

    // next line br
    if (currentWidth + offsetWidth >= clientWidth || (tagName === 'BR' && currentIndex !== 0)) {
      currentWidth = 0;
      currentIndex++;
    }

    lines[currentIndex] = lines[currentIndex] || [];
    const line = lines[currentIndex];

    if (tagName !== 'BR') line.push(outerHTML);
    currentWidth += offsetWidth;
  });

  return lines.map(line =>
    line.length
      ? `<div style="position:relative;display:block;text-align:start;">${line.join('')}</div>`
      : `<div style="position:relative;display:block;text-align:start;color:transparent">.</div>`
  );
};

const fixLinkStyle = () => {
  const paragraphElem = document.getElementsByClassName('portrait-paragraph-in-two-columns__text');
  const captionElem = document.getElementsByClassName('portrait-paragraph-in-two-columns__caption');

  Array.from(paragraphElem).forEach(pgElem => {
    inheritChildStyle(pgElem, 'a', 'borderColor');
  });

  Array.from(captionElem).forEach(cpElem => {
    inheritChildStyle(cpElem, 'a', 'color');
  });
};

const PortraitParagraphInTwoColumns = ({ data, endCard }) => {
  const { backgroundColor, paragraphC18, header, barColor, caption } = data;
  const [isMobile, setIsMobile] = useState(false); // TODO use a media breakpoint

  const paragraphPlaceholderRef = useRef();
  const [columns, setColumns] = useState([]);

  const splitParagraph = useCallback(() => {
    if (!(window.innerWidth < breakpoints.tablet)) return;
    if (!paragraphPlaceholderRef.current) return;

    paragraphPlaceholderRef.current.innerHTML = paragraphC18;

    const { childNodes } = paragraphPlaceholderRef.current;
    const lines = [];
    childNodes.forEach((childNode, index) => {
      lines.push(...splitIntoLines(childNode, index === 0));
    });

    // ratio is constraint on design
    // C18 paragraph height and overall height
    // 121 and 88 are padding top and bottom of the container and 74 is the header
    const firstContainerNoOfLines = Math.floor((window.innerHeight - (121 + 88 + 74)) / 24);
    // 104 and 88 are padding top and bottom of the container
    const followingContainerNoOfLines = Math.floor((window.innerHeight - (104 + 88)) / 24);

    const columns = [];
    while (lines.length) {
      const noOfLines = !columns.length ? firstContainerNoOfLines : followingContainerNoOfLines;
      const columnLines = lines.splice(0, noOfLines);
      columns[columns.length] = columnLines;
    }
    setColumns(columns);
  }, [isMobile, paragraphC18]);

  // TODO simplify this initialization effect
  useEffect(() => {
    const listener = debounce(() => {
      splitParagraph();
      setIsMobile(window.innerWidth < breakpoints.tablet);
    }, 500);
    setIsMobile(window.innerWidth < breakpoints.tablet);
    splitParagraph();
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [splitParagraph]);

  useEffect(() => fixLinkStyle(), []);
  return isMobile ? (
    <>
      <ArticleCardBase
        key="portrait-paragraph-in-two-columns-1"
        variation="portrait"
        accentColor1={backgroundColor}
        endCard={endCard}>
        <section className="card-container portrait-paragraph-in-two-columns">
          <div className="portrait-paragraph-in-two-columns__inner">
            <div
              className="portrait-paragraph-in-two-columns__header"
              dangerouslySetInnerHTML={{ __html: header }}
            />
            <div className="portrait-paragraph-in-two-columns__text">
              <p
                ref={paragraphPlaceholderRef}
                className="portrait-paragraph-in-two-columns__text__paragraph"
                style={{ height: '0px', overflow: 'hidden' }}
              />
              {columns[0] ? (
                <p
                  className="portrait-paragraph-in-two-columns__text__paragraph"
                  dangerouslySetInnerHTML={{ __html: columns[0].join('') }}
                />
              ) : null}
            </div>
            {columns.length === 1 && (
              <div
                className="portrait-paragraph-in-two-columns__caption"
                dangerouslySetInnerHTML={{ __html: caption }}
              />
            )}
          </div>
        </section>
      </ArticleCardBase>
      {columns.slice(1).map((column, index) => (
        <ArticleCardBase
          key={`portrait-paragraph-in-two-columns-${index + 1}`}
          variation="portrait"
          accentColor1={backgroundColor}>
          <section className="card-container portrait-paragraph-in-two-columns portrait-paragraph-in-two-columns--second">
            <div className="portrait-paragraph-in-two-columns__inner">
              <div className="portrait-paragraph-in-two-columns__text">
                <p dangerouslySetInnerHTML={{ __html: column.join('') }} />
              </div>
              {index === columns.slice(1).length - 1 ? (
                <div
                  className="portrait-paragraph-in-two-columns__caption"
                  dangerouslySetInnerHTML={{ __html: caption }}
                />
              ) : null}
            </div>
          </section>
        </ArticleCardBase>
      ))}
    </>
  ) : (
    <ArticleCardBase variation="portrait" accentColor1={backgroundColor}>
      <section className="card-container portrait-paragraph-in-two-columns">
        <div className="portrait-paragraph-in-two-columns__inner">
          <div
            className="portrait-paragraph-in-two-columns__header"
            dangerouslySetInnerHTML={{ __html: header }}
          />
          <div
            className="portrait-paragraph-in-two-columns__text"
            dangerouslySetInnerHTML={{ __html: paragraphC18 }}
          />
          <span
            className="portrait-paragraph-in-two-columns__barline"
            style={{ backgroundColor: barColor }}
          />
          <div
            className="portrait-paragraph-in-two-columns__caption"
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        </div>
      </section>
    </ArticleCardBase>
  );
};

export default PortraitParagraphInTwoColumns;
